<script lang="ts">
    import type {Capability, DocumentAvailability} from '../../typings/portaro.be.types';
    import {getContext} from 'svelte';
    import {CONTEXT_PATH_KEY, DATE_FORMATTER_KEY, LOCALIZE_KEY, pipe} from '../../svelte/utils';
    import {getFirstByProperty} from '../../shared/utils/array-utils.fp';
    import type {DateFormatter, LocalizeFunction} from '../../svelte/types';
    import KpPluralizeText from '../../commons/components/kp-pluralize-text/KpPluralizeText.svelte';
    import {inline, prop, strParams} from '../../svelte/pipes';

    export let availability: DocumentAvailability;
    export let showExemplarsInfo = true;

    const localize: LocalizeFunction = getContext(LOCALIZE_KEY);
    const dateFormatter: DateFormatter = getContext(DATE_FORMATTER_KEY);
    const CONTEXT_PATH: string = getContext(CONTEXT_PATH_KEY);

    const isDownloadable = !!getFirstByProperty<Capability, keyof Capability>('name', 'downloadability')(availability.capabilities);
    const isEReadingLoanable = !!getFirstByProperty<Capability, keyof Capability>('name', 'ereadingLoanability')(availability.capabilities);
    const isFlexiBooksLoanable = !!getFirstByProperty<Capability, keyof Capability>('name', 'flexibooksLoanability')(availability.capabilities);
    const isLevnaKnihovnaLoanable = !!getFirstByProperty<Capability, keyof Capability>('name', 'levnaknihovnaLoanability')(availability.capabilities);

    const showAvailabilityInfo = availability.reasonable && (availability.free || availability.reservable || !isDownloadable);

    const availableAtDepartmentsCount = availability.buildingsToOrderOn.length + availability.buildingsToPickupOn.length;
</script>

<template>
    {#if showAvailabilityInfo}
        <div class="availability-exemplars-info">

            <!-- ZELENE NEBO CERVENE KOLECKO -->
            {#if availability.freeItemsCount === 0}
                <img src={`${CONTEXT_PATH}/resources/img/icons/availability_no.gif`} width="14" height="14" alt="{localize(/* @kp-localization loan.NelzeVypujcit */ 'loan.NelzeVypujcit')}"/>
            {:else if availability.freeItemsCount > 0}
                <img src={`${CONTEXT_PATH}/resources/img/icons/availability_yes.gif`} width="14" height="14" alt="{localize(/* @kp-localization loan.Available */ 'loan.Available')}"/>
            {/if}

            <!-- TEXT x k vypujceni, y prezencne z z exmplaru NEBO zadne exemplare -->
            {#if availability.obtainableItemsCount === 0}
                <span class="availability-no-exemplars-text">
                    {localize(/* @kp-localization loan.DokumentNemaExemplare */ 'loan.DokumentNemaExemplare')}
                </span>
            {:else if availability.obtainableItemsCount > 0}
                <span class="availability-obtainable-exemplars-text">
                    <span class="sr-only">{localize(/* @kp-localization commons.Exemplare */ 'commons.Exemplare')}: </span>
                    {pipe(localize(/* @kp-localization loan.XKVypujceniAYPrezencneZZ */ 'loan.XKVypujceniAYPrezencneZZ'),
                          strParams(availability.freeTakeawayItemsCount.toString(), availability.freeVisitableItemsCount.toString(), availability.obtainableItemsCount.toString()))}
                </span>
            {/if}

            {#if isEReadingLoanable || isFlexiBooksLoanable || isLevnaKnihovnaLoanable}
                <img src={`${CONTEXT_PATH}/resources/img/icons/ebook.png`} width="18" height="18" alt="{localize(/* @kp-localization loan.eVypujcka */ 'loan.eVypujcka')}"/>
                {localize(/* @kp-localization loan.eVypujcka */ 'loan.eVypujcka')}
            {/if}

            {#if showExemplarsInfo}
                <!-- SEZNAM BUDOV S EXEMPLARI, zobrazeno pomoci custom stylu v KFBZ -->
                <div class="availability-exemplars-buildings-info">
                    {#if availableAtDepartmentsCount > 0}
                        <KpPluralizeText
                                count={availableAtDepartmentsCount}
                                oneCountText={localize(/* @kp-localization loan.VolnyNaBudove */ 'loan.VolnyNaBudove')}
                                moreText={localize(/* @kp-localization loan.VolnyNaBudovach */ 'loan.VolnyNaBudovach')}
                        />
                        {pipe(availability.buildingsToOrderOn.concat(availability.buildingsToPickupOn), prop('text'), inline())}
                    {/if}

                    {#if availability.obtainable}
                        {#if availableAtDepartmentsCount > 0}
                            <!--byl vypsan seznam budov s absencne volnymi exemplari-->
                            {localize(/* @kp-localization loan.NaOstatnichBudovach */ 'loan.NaOstatnichBudovach')}
                        {/if}

                        <KpPluralizeText
                                count={availability.reservationsCount}
                                zeroCountText={localize(/* @kp-localization loan.DokumentNeniZarezervovan */ 'loan.DokumentNeniZarezervovan')}
                                moreText={localize(/* @kp-localization loan.DokumentJeXxZarezervovan */ 'loan.DokumentJeXxZarezervovan')}
                        />
                    {/if}
                </div>
            {/if}

            {#if availability.lastUserLoanDate}
                <div class="last-user-loan-date text-muted small">
                    {localize(/* @kp-localization loan.Vypujceno */ 'loan.Vypujceno')} {pipe(availability.lastUserLoanDate, dateFormatter('d.M.yyyy'))}
                </div>
            {/if}

        </div>
    {/if}
</template>

<style lang="less">
  .availability-exemplars-info {
    display: inline-block;
    vertical-align: middle;
  }

  .availability-exemplars-buildings-info {
    color: gray;
  }
</style>